import { useState, useEffect, useRef } from 'react';
import { supabase } from '@/lib/supabase';
import { AdminPageLayout } from '@/components/layout/AdminPageLayout';
import { User, Mic, MicOff, Pause, Play, Phone, PhoneOff, AlertCircle } from 'lucide-react';
import { useTwilioDevice } from '@/hooks/useTwilioDevice';
import { useAgentAudioCapture } from '@/hooks/useAgentAudioCapture';
import { Dialpad } from '@/components/agent-console/Dialpad';
import { InteractionsPanel } from '@/components/agent-console/InteractionsPanel';
import { RejectReasonModal } from '@/components/agent-console/RejectReasonModal';
import { useTheme } from '@/contexts/ThemeContext';
import { useCurrentAgent, useUpdateAgentStatus, useAgentStatusSubscription } from '@/lib/hooks';
import { useAllPhones } from '@/lib/hooks/useQueues';
import { useUpsertCustomerByPhone } from '@/lib/hooks/useCustomers';
import type { AgentStatus } from '@/lib/repositories/AgentRepository';

// Match colors from AdminLayout exactly
const STATUS_COLORS: Record<AgentStatus, string> = {
  available: '#10b981',
  busy: '#ef4444',
  away: '#6b7280',
  break: '#f59e0b',
  meal: '#f97316',
  meeting: '#8b5cf6',
  training: '#3b82f6',
  offline: '#9ca3af'
};

// NOTE: This component is DEPRECATED
// The calling interface has been moved to AdminLayout (header) with LiveCoachingPanel
// This file is kept for reference but should not be used in production

export default function AgentConsole() {
  const { theme } = useTheme();
  
  return (
    <AdminPageLayout
      title="☎️ Agent Console (Deprecated)"
      subtitle="This page is deprecated. Calling is now integrated into the header."
    >
      <div className="flex items-center justify-center h-[calc(100vh-12rem)]">
        <div 
          className="rounded-lg p-8 max-w-2xl text-center"
          style={{
            backgroundColor: theme.colors.warningBg,
            border: `2px solid ${theme.colors.warningBorder}`
          }}
        >
          <AlertCircle className="w-16 h-16 mx-auto mb-4" style={{ color: theme.colors.warningText }} />
          <h2 className="text-2xl font-bold mb-4" style={{ color: theme.colors.textPrimary }}>
            This Page is Deprecated
          </h2>
          <p className="mb-4" style={{ color: theme.colors.textSecondary }}>
            The Agent Console has been integrated into the main layout for a better user experience.
          </p>
          <p className="text-sm" style={{ color: theme.colors.textSecondary }}>
            All calling functionality is now available in the header across all pages:
          </p>
          <ul className="mt-4 text-left space-y-2 max-w-md mx-auto" style={{ color: theme.colors.textSecondary }}>
            <li>• Accept/reject incoming calls from the header alert</li>
            <li>• Make outbound calls using the "Make Call" button</li>
            <li>• Access live transcription and AI coaching via the slide-in panel</li>
            <li>• Complete post-work and set call outcomes</li>
            <li>• Change your status anytime</li>
          </ul>
        </div>
      </div>
    </AdminPageLayout>
  );
}
